import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import {
  Dialog,
  CircularProgress,
  DialogContent,
  Typography
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import mergePeriodRulesIntoScheduleRules from '~/modules/resourcing/common/util/mergeScheduleUtil';
import useGetExistingRoleUserAllocations from './useGetExistingRoleUserAllocations';
import ResourceMergeDialog from './ResourceMergeDialog';

const useStyles = makeStyles(theme => ({
  loadingContent: {
    display: 'flex'
  },
  loadingDescription: {
    marginLeft: theme.spacing(1),
    alignSelf: 'center'
  }
}));

const ShowResourceMergeDialogOrLoading = ({
  resourceMergeDialogOpen,
  closeResourceMergeDialog,
  resourceMergeDialogAllocation,
  onSubmit
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const {
    user: { id: userUri }
  } = resourceMergeDialogAllocation;

  const roleUri = resourceMergeDialogAllocation?.role?.id || 'null';
  const projectUri =
    resourceMergeDialogAllocation.resourceAllocation.project?.id ||
    resourceMergeDialogAllocation.projectUri ||
    resourceMergeDialogAllocation?.project.id;

  const { resourceAllocations, isLoading } = useGetExistingRoleUserAllocations({
    roleUri,
    projectUri,
    userUri
  });

  const onConfirm = useCallback(async () => {
    const existingResourceAllocationScheduleRules =
      resourceAllocations?.length && resourceAllocations[0].scheduleRules;

    const mergedAllocations = mergePeriodRulesIntoScheduleRules(
      existingResourceAllocationScheduleRules
    )(resourceMergeDialogAllocation.scheduleRules);

    await onSubmit({
      overrideScheduleRules: mergedAllocations,
      resourceAllocation:
        resourceAllocations &&
        resourceAllocations.length &&
        resourceAllocations[0],
      isMerge: true
    });
  }, [
    onSubmit,
    resourceAllocations,
    resourceMergeDialogAllocation.scheduleRules
  ]);

  if (isLoading) {
    return (
      <Dialog open={resourceMergeDialogOpen} onClose={closeResourceMergeDialog}>
        <DialogContent className={classes.loadingContent}>
          <CircularProgress size={24} />
          <Typography variant="body2" className={classes.loadingDescription}>
            {formatMessage({ id: 'resourceMergeDialog.allocatingResource' })}
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  if (
    resourceAllocations?.length === 0 ||
    resourceMergeDialogAllocation.resourceRequestId
  ) {
    closeResourceMergeDialog();
    onSubmit({
      overrideScheduleRules: resourceMergeDialogAllocation.scheduleRules,
      isMerge: false
    });

    return null;
  }

  return (
    <ResourceMergeDialog
      resourceMergeDialogOpen={resourceMergeDialogOpen}
      closeResourceMergeDialog={closeResourceMergeDialog}
      resourceMergeDialogAllocation={resourceMergeDialogAllocation}
      onConfirm={onConfirm}
    />
  );
};

ShowResourceMergeDialogOrLoading.propTypes = {
  resourceMergeDialogOpen: PropTypes.bool.isRequired,
  closeResourceMergeDialog: PropTypes.func.isRequired,
  resourceMergeDialogAllocation: PropTypes.object,
  onSubmit: PropTypes.func.isRequired
};

export default ShowResourceMergeDialogOrLoading;
